import { DestroyRef, inject, linkedSignal, untracked } from '@angular/core';
import { UmmeldungStore } from '../ummeldung.store';
import { isOfEventType, WIZARD_CONTROLLER, WizardEventType } from '@mwe/features/wizard';
import { FieldTree, form, SchemaOrSchemaFn } from '@angular/forms/signals';
import { Ummeldung } from '../../../../generated/model/ummeldung.model';
import { UmmeldungStoreType } from '../models';
import { UmmeldungWizardService } from '../ummeldung.wizard.service';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export const DEFAULT_SYNC_EVENTS = [WizardEventType.BeforeNextStepNavigation, WizardEventType.BeforePrevStepNavigation];

export interface RegisteredWizardStepForm {
  readonly field: FieldTree<unknown>;
  readonly commitDraft: () => void;
}

export interface WizardStepFormOptions<TFormModel> {
  /** Maps the persisted Ummeldung (store truth) into this step's editable form model. */
  fromStore: (ummeldung: Ummeldung | null, store: UmmeldungStoreType) => TFormModel;
  /** Maps the edited form model back into a partial Ummeldung to patch onto the store. */
  toStore: (draft: TFormModel, store: UmmeldungStoreType) => Partial<Ummeldung>;
  /** Signal Forms schema with this step's validation rules. */
  schema?: SchemaOrSchemaFn<TFormModel>;
  /** Wizard events on which the draft is written back to the store. Defaults to next/prev navigation. */
  syncOn?: WizardEventType[];
}

/**
 * The one way an ummeldung-wizard step edits the Ummeldung.
 *
 * The `UmmeldungStore` remains the single source of persisted truth (loading, resume, save,
 * session storage). Each step owns a short-lived draft in the shape its UI needs — which is
 * deliberately *not* the DTO shape — realized as a Signal Forms tree:
 *
 *   store --fromStore()--> draft signal --form(schema)--> template
 *   store <--toStore()--- draft
 *
 */
export function wizardStepForm<TFormModel>(options: WizardStepFormOptions<TFormModel>): FieldTree<TFormModel> {
  const store = inject(UmmeldungStore);
  const wizardController = inject(WIZARD_CONTROLLER);
  const ummeldungWizardService = inject(UmmeldungWizardService);
  const destroyRef = inject(DestroyRef);

  const draft = linkedSignal({
    source: store.dataReady,
    computation: () => options.fromStore(untracked(store.activeUmmeldung), store),
  });
  const formTree = options.schema !== undefined ? form(draft, options.schema) : form(draft);
  const commitDraft = () => store.updateActiveUmmeldung(options.toStore(draft(), store));
  const registration = ummeldungWizardService.registerCurrentStepForm({ field: formTree as FieldTree<unknown>, commitDraft });

  destroyRef.onDestroy(registration.unregister);

  wizardController.events$
    .pipe(
      filter(
        event => ummeldungWizardService.isCurrentStep(registration.stepId) && isOfEventType(event, options.syncOn ?? DEFAULT_SYNC_EVENTS),
      ),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe(() => commitDraft());

  return formTree;
}
